import LocationsAppointmentRequiredQuestionBubble from "@/Shared/Locations/Bubbles/LocationsAppointmentRequiredQuestionBubble";
import LocationsInsideQuestionBubble from "@/Shared/Locations/Bubbles/LocationsInsideQuestionBubble";
import LocationsLiftGateRequiredQuestionBubble from "@/Shared/Locations/Bubbles/LocationsLiftGateRequiredQuestionBubble";
import { accessorialUnavailableForLocationType } from "@/Shared/Locations/Helpers/recommendedAccessorialsForLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import type { ReactElement } from "react";

export interface UnscopedAccessorialType {
  name: string;
  counterPickup: string;
  counterDelivery: string;
  pickupName: string;
  deliveryName: string;
  description: string;
  pickupDescription: string;
  deliveryDescription: string;
  type: string;
  comingSoon?: boolean | undefined;
  /** We don't offer this service at this location type at all — see [unavailable]. */
  unavailable?: boolean | undefined;
  questionBubble: (locationContext: LocationContext) => ReactElement;
}

export interface AccessorialType {
  name: string;
  description: string;
  type: string;
  comingSoon?: boolean | undefined;
  unavailable?: boolean | undefined;
  questionBubble: (locationContext: LocationContext) => ReactElement;
}

export function generateAccessorialTypes(
  locationType: LocationType | undefined,
): Array<UnscopedAccessorialType> {
  const unavailableForResidential = accessorialUnavailableForLocationType(
    locationType,
    "LOGISTICS_INSIDE",
  );

  return [
    // LOGISTICS_LIFT_GATE_REQUIRED
    {
      name: "Lift gate required on truck",
      counterPickup: "No Lift Gate Required",
      counterDelivery: "No Lift Gate Required",
      pickupName: "Lift gate required on truck",
      deliveryName: "Lift gate required on truck",
      description: "The location does not have a loading dock",
      pickupDescription: "The pickup location does not have a loading dock",
      deliveryDescription: "The delivery location does not have a loading dock",
      type: "LOGISTICS_LIFT_GATE_REQUIRED",
      questionBubble: (locationContext: LocationContext) => (
        <LocationsLiftGateRequiredQuestionBubble
          locationContext={locationContext}
        />
      ),
    },

    // LOGISTICS_INSIDE
    {
      name: "Inside location",
      counterPickup: "No Inside Pickup",
      counterDelivery: "No Inside Delivery",
      pickupName: "Pickup inside the location",
      deliveryName: "Delivery inside the location",
      description: "When the freight is located inside the building",
      pickupDescription:
        "The freight needs to be brought from inside the building",
      deliveryDescription:
        "The freight needs to be brought inside the building",
      type: "LOGISTICS_INSIDE",
      unavailable: unavailableForResidential,
      questionBubble: (locationContext: LocationContext) => (
        <LocationsInsideQuestionBubble
          locationContext={locationContext}
          unavailableForResidential={unavailableForResidential}
        />
      ),
    },

    // SCHEDULING_APPOINTMENT_REQUIRED
    {
      name: "Delivery appointment required",
      counterPickup: "No Pickup Appointment",
      counterDelivery: "No Delivery Appointment",
      pickupName: "n/a",
      deliveryName: "Delivery appointment required",
      pickupDescription: "n/a",
      deliveryDescription:
        locationType === LocationType.DistributionWarehouse
          ? "A delivery appointment is required for distribution warehouses"
          : "The location requires the carrier to make a delivery appointment",
      description:
        "The location requires the carrier to make a delivery appointment",
      type: "SCHEDULING_APPOINTMENT_REQUIRED",
      questionBubble: (locationContext: LocationContext) => (
        <LocationsAppointmentRequiredQuestionBubble
          locationContext={locationContext}
        />
      ),
    },

    // Jan 2021: Only VanKam supports After hours through an API, so removing it for now
    // SCHEDULING_AFTER_HOURS
    // {
    //     name: 'After hours',
    //     pickupName: 'After hours pickup',
    //     deliveryName: 'After hours delivery',
    //     pickupDescription:
    //         'Pickup is required outside of normal business hours (9am-5pm)',
    //     deliveryDescription:
    //         'Delivery is required outside of normal business hours (9am-5pm)',
    //     description:
    //         'Access required outside of normal business hours (9am-5pm)',

    //     type: 'SCHEDULING_AFTER_HOURS',
    //     comingSoon: true,
    //     questionBubble: (locationContext?: LocationContext) => (
    //         <AfterHoursQuestionBubble locationContext={locationContext} />
    //     ),
    // },
  ];
}
